import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronRight, Edit2, RefreshCw, Trash2, X } from 'lucide-react';
import './AdminCategoriesManagement.css';
import { API_BASE_URL } from '../../api';

console.log('AdminCategoriesManagement - API_BASE_URL:', API_BASE_URL);
console.log('AdminCategoriesManagement - Environment variables:', {
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV
});

const MAX_SELECTED_IMAGE_BYTES = 5 * 1024 * 1024;
const PROXY_SAFE_IMAGE_BYTES = 900 * 1024;
const MAX_IMAGE_DIMENSION = 1600;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);
const OPTIMIZABLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const ALLOWED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;
const OPTIMIZABLE_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp)$/i;

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Could not read selected image'));
  reader.readAsDataURL(file);
});

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Could not process selected image'));
  };
  image.src = objectUrl;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('Could not optimize selected image'));
  }, type, quality);
});

const getOptimizedFileName = (file, mimeType) => {
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
  const extension = mimeType === 'image/jpeg' ? 'jpg' : 'webp';
  return `${baseName}.${extension}`;
};

const createCompressedImageBlob = async (canvas, quality) => {
  const webpBlob = await canvasToBlob(canvas, 'image/webp', quality).catch(() => null);
  if (webpBlob?.type === 'image/webp') {
    return webpBlob;
  }

  return canvasToBlob(canvas, 'image/jpeg', quality);
};

const optimizeImageForUpload = async (file) => {
  if (file.size <= PROXY_SAFE_IMAGE_BYTES) return { file, optimized: false };

  if (!OPTIMIZABLE_IMAGE_TYPES.has(file.type) && !OPTIMIZABLE_IMAGE_EXTENSIONS.test(file.name)) {
    throw new Error('GIF images must be under 900KB. Please choose a smaller file.');
  }

  const image = await loadImageFromFile(file);
  const initialScale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.width, image.height));
  let width = Math.max(1, Math.round(image.width * initialScale));
  let height = Math.max(1, Math.round(image.height * initialScale));
  let quality = 0.82;
  let bestBlob = null;

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not optimize selected image');
    }
    context.drawImage(image, 0, 0, width, height);

    const blob = await createCompressedImageBlob(canvas, quality);
    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }
    if (blob.size <= PROXY_SAFE_IMAGE_BYTES) {
      break;
    }

    if (quality > 0.5) {
      quality = Math.max(0.5, quality - 0.12);
    } else {
      width = Math.max(1, Math.round(width * 0.82));
      height = Math.max(1, Math.round(height * 0.82));
      quality = 0.78;
    }
  }

  if (!bestBlob || bestBlob.size > PROXY_SAFE_IMAGE_BYTES) {
    throw new Error('Image is too large to optimize. Please choose a smaller JPG, PNG or WEBP file.');
  }

  return {
    file: new File([bestBlob], getOptimizedFileName(file, bestBlob.type), {
      type: bestBlob.type || 'image/webp',
      lastModified: Date.now()
    }),
    optimized: true
  };
};

const prepareImageForUpload = async (file) => {
  if (!ALLOWED_IMAGE_TYPES.has(file.type) && !ALLOWED_IMAGE_EXTENSIONS.test(file.name)) {
    throw new Error('Please select a JPG, PNG, WEBP or GIF image');
  }

  if (file.size > MAX_SELECTED_IMAGE_BYTES) {
    throw new Error('Image size should be less than 5MB');
  }

  return optimizeImageForUpload(file);
};

const AdminCategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    hsn_code: '',
    gst_rate: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null); // Store category ID for inline confirmation
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const initialNewCategoryState = { name: '', hsn_code: '', gst_rate: '' };
  const [newCategory, setNewCategory] = useState(initialNewCategoryState);
  const [newCategoryImage, setNewCategoryImage] = useState(null);
  const [newCategoryPreview, setNewCategoryPreview] = useState(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const newCategoryImageInputRef = useRef(null);
  const initialSubcategoryState = { name: '', description: '', is_active: true };
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [subcategoryForm, setSubcategoryForm] = useState(initialSubcategoryState);
  const [subcategoryImage, setSubcategoryImage] = useState(null);
  const [subcategoryImagePreview, setSubcategoryImagePreview] = useState(null);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [savingSubcategory, setSavingSubcategory] = useState(false);
  const [deletingSubcategoryId, setDeletingSubcategoryId] = useState(null);
  const subcategoryImageInputRef = useRef(null);

  // Toast notifications
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  // Get admin headers
  const adminHeaders = () => {
    const adminToken = window.localStorage.getItem('adminToken');
    const vendorToken = window.localStorage.getItem('token');
    const legacyKey = window.localStorage.getItem('ADMIN_API_KEY') || 
                     window.localStorage.getItem('adminApiKey') ||
                     process.env.REACT_APP_ADMIN_API_KEY ||
                     'dev-admin-key-sonic-cart-2024-secure'; // Fallback to default key
    
    const headers = { 'Content-Type': 'application/json' };
    if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`;
    else if (vendorToken) headers['Authorization'] = `Bearer ${vendorToken}`;
    if (legacyKey) headers['x-admin-key'] = legacyKey;
    
    return headers;
  };

  const adminAuthHeaders = () => {
    const headers = {};
    const adminToken = window.localStorage.getItem('adminToken');
    const vendorToken = window.localStorage.getItem('token');
    const legacyKey = window.localStorage.getItem('ADMIN_API_KEY') || 
                     window.localStorage.getItem('adminApiKey') ||
                     process.env.REACT_APP_ADMIN_API_KEY ||
                     'dev-admin-key-sonic-cart-2024-secure'; // Fallback to default key
    
    if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`;
    else if (vendorToken) headers['Authorization'] = `Bearer ${vendorToken}`;
    if (legacyKey) headers['x-admin-key'] = legacyKey;
    
    console.log('Admin auth headers:', {
      hasToken: !!headers['Authorization'],
      hasAdminKey: !!headers['x-admin-key'],
      adminKey: legacyKey ? legacyKey.substring(0, 10) + '...' : 'none'
    });
    
    return headers;
  };

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching categories from /api/categories');
      const res = await fetch(`${API_BASE_URL}/api/categories`, { headers: adminHeaders() });
      console.log('Response status:', res.status, res.statusText);
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const errorMsg = data.error || data.message || `Failed to fetch categories (${res.status})`;
        console.error('Error response:', data);
        throw new Error(errorMsg);
      }
      const data = await res.json();
      console.log('Categories fetched:', data.categories?.length || 0);
      // Log image paths for debugging
      data.categories?.forEach((cat, index) => {
        if (cat.category_image) {
          console.log(`Category ${index + 1} (${cat.name}): image = ${cat.category_image}`);
        }
      });
      setCategories(data.categories || []);
    } catch (e) {
      console.error('Error in fetchCategories:', e);
      const errorMsg = e.message || 'Failed to fetch categories';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm(initialSubcategoryState);
    setSubcategoryImage(null);
    setSubcategoryImagePreview(null);
    setEditingSubcategoryId(null);
    if (subcategoryImageInputRef.current) {
      subcategoryImageInputRef.current.value = '';
    }
  };

  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) return;
    try {
      setSubcategoriesLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/categories/${categoryId}/subcategories`, {
        headers: adminHeaders()
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Failed to fetch subcategories (${res.status})`);
      }

      const data = await res.json();
      setSubcategoriesByCategory(prev => ({
        ...prev,
        [categoryId]: data.subcategories || []
      }));
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      showToast(err.message || 'Failed to fetch subcategories', 'error');
      setSubcategoriesByCategory(prev => ({
        ...prev,
        [categoryId]: prev[categoryId] || []
      }));
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  const toggleSubcategories = async (category) => {
    if (expandedCategoryId === category.id) {
      setExpandedCategoryId(null);
      resetSubcategoryForm();
      return;
    }

    setExpandedCategoryId(category.id);
    resetSubcategoryForm();
    if (!subcategoriesByCategory[category.id]) {
      await fetchSubcategories(category.id);
    }
  };

  const handleSubcategoryImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const prepared = await prepareImageForUpload(file);
      setSubcategoryImage(prepared.file);
      setSubcategoryImagePreview(await readFileAsDataUrl(prepared.file));
      if (prepared.optimized) {
        showToast('Image optimized for upload', 'success');
      }
    } catch (err) {
      event.target.value = '';
      showToast(err.message || 'Could not prepare image for upload', 'error');
    }
  };

  const editSubcategory = (subcategory) => {
    setEditingSubcategoryId(subcategory.id);
    setSubcategoryForm({
      name: subcategory.name || '',
      description: subcategory.description || '',
      is_active: Boolean(Number(subcategory.is_active))
    });
    setSubcategoryImage(null);
    setSubcategoryImagePreview(subcategory.subcategory_image || subcategory.image_url || subcategory.icon_url || null);
    if (subcategoryImageInputRef.current) {
      subcategoryImageInputRef.current.value = '';
    }
  };

  const handleSaveSubcategory = async (event, categoryId) => {
    event.preventDefault();
    if (!categoryId) return;

    const trimmedName = subcategoryForm.name.trim();
    if (!trimmedName) {
      showToast('Subcategory name is required', 'error');
      return;
    }

    try {
      setSavingSubcategory(true);
      const body = new FormData();
      body.append('name', trimmedName);
      body.append('description', subcategoryForm.description || '');
      body.append('is_active', subcategoryForm.is_active ? '1' : '0');
      if (subcategoryImage) {
        body.append('subcategory_image', subcategoryImage);
      }

      const url = editingSubcategoryId
        ? `${API_BASE_URL}/api/categories/${categoryId}/subcategories/${editingSubcategoryId}`
        : `${API_BASE_URL}/api/categories/${categoryId}/subcategories`;
      const method = editingSubcategoryId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: adminAuthHeaders(),
        body
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Failed to save subcategory (${res.status})`);
      }

      resetSubcategoryForm();
      await fetchSubcategories(categoryId);
      await fetchCategories();
      showToast(editingSubcategoryId ? 'Subcategory updated successfully' : 'Subcategory added successfully', 'success');
    } catch (err) {
      console.error('Error saving subcategory:', err);
      const message = err.message === 'Failed to fetch'
        ? 'Upload failed before reaching the API. Try a smaller image or check the server upload limit.'
        : err.message || 'Failed to save subcategory';
      showToast(message, 'error');
    } finally {
      setSavingSubcategory(false);
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryId) => {
    if (!categoryId || !subcategoryId) return;

    try {
      setDeletingSubcategoryId(subcategoryId);
      const res = await fetch(`${API_BASE_URL}/api/categories/${categoryId}/subcategories/${subcategoryId}`, {
        method: 'DELETE',
        headers: adminHeaders()
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Failed to delete subcategory (${res.status})`);
      }

      await fetchSubcategories(categoryId);
      await fetchCategories();
      if (editingSubcategoryId === subcategoryId) {
        resetSubcategoryForm();
      }
      showToast('Subcategory deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting subcategory:', err);
      showToast(err.message || 'Failed to delete subcategory', 'error');
    } finally {
      setDeletingSubcategoryId(null);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Open edit form inline
  const handleEdit = (category) => {
    // If clicking edit on the same category, close it
    if (editingCategoryId === category.id) {
      closeEditForm();
      return;
    }
    setEditingCategoryId(category.id);
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      hsn_code: category.hsn_code || '',
      gst_rate: category.gst_rate !== null && category.gst_rate !== undefined ? String(category.gst_rate) : ''
    });
    setSelectedImage(null);
    setImagePreview(category.category_image || null);
  };

  // Close edit form function
  const closeEditForm = useCallback(() => {
    if (updating) return; // Prevent closing during update
    const currentEditingId = editingCategoryId;
    setEditingCategoryId(null);
    setEditingCategory(null);
    setFormData({ name: '', hsn_code: '', gst_rate: '' });
    setSelectedImage(null);
    setImagePreview(null);
    // Clear file input after state is cleared
    setTimeout(() => {
      if (currentEditingId) {
        const fileInput = document.getElementById(`category-image-input-${currentEditingId}`);
        if (fileInput) {
          fileInput.value = '';
        }
      }
    }, 0);
  }, [updating, editingCategoryId]);

  // Close on Escape key
  useEffect(() => {
    if (editingCategoryId && !updating) {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeEditForm();
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [editingCategoryId, updating, closeEditForm]);

  // Handle image file selection
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const prepared = await prepareImageForUpload(file);
        setSelectedImage(prepared.file);
        setImagePreview(await readFileAsDataUrl(prepared.file));
        if (prepared.optimized) {
          showToast('Image optimized for upload', 'success');
        }
      } catch (err) {
        e.target.value = '';
        showToast(err.message || 'Could not prepare image for upload', 'error');
      }
    }
  };

  const handleNewCategoryImageChange = async (e) => {
    const file = e.target.files[0];
    console.log('handleNewCategoryImageChange called with file:', file);
    
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    try {
      const prepared = await prepareImageForUpload(file);
      console.log('Setting new category image...', {
        originalSize: file.size,
        uploadSize: prepared.file.size,
        uploadType: prepared.file.type
      });
      setNewCategoryImage(prepared.file);
      setNewCategoryPreview(await readFileAsDataUrl(prepared.file));
      if (prepared.optimized) {
        showToast('Image optimized for upload', 'success');
      }
    } catch (err) {
      console.error('Image preparation error:', err);
      e.target.value = '';
      showToast(err.message || 'Could not prepare image for upload', 'error');
    }
  };

  const resetNewCategoryForm = () => {
    setNewCategory(initialNewCategoryState);
    setNewCategoryImage(null);
    setNewCategoryPreview(null);
    if (newCategoryImageInputRef.current) {
      newCategoryImageInputRef.current.value = '';
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const trimmedName = newCategory.name.trim();
    if (!trimmedName) {
      showToast('Category name is required', 'error');
      return;
    }

    try {
      setCreatingCategory(true);
      
      console.log('Creating category with data:', {
        name: trimmedName,
        hsn_code: newCategory.hsn_code,
        hasImage: !!newCategoryImage,
        imageSize: newCategoryImage ? newCategoryImage.size : 0,
        imageType: newCategoryImage ? newCategoryImage.type : 'none'
      });
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', trimmedName);
      formDataToSend.append('hsn_code', newCategory.hsn_code || '');
      formDataToSend.append('gst_rate', newCategory.gst_rate !== '' && newCategory.gst_rate !== undefined ? newCategory.gst_rate : '0');
      
      if (newCategoryImage) {
        console.log('Appending image to FormData:', {
          name: newCategoryImage.name,
          size: newCategoryImage.size,
          type: newCategoryImage.type
        });
        formDataToSend.append('category_image', newCategoryImage);
      } else {
        console.log('No image selected for upload');
      }

      // Log FormData contents (for debugging)
      console.log('FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }

      const headers = adminAuthHeaders();
      console.log('Request headers:', headers);
      console.log('Request URL:', `${API_BASE_URL}/api/categories`);

      const res = await fetch(`${API_BASE_URL}/api/categories`, {
        method: 'POST',
        headers: headers, // Don't set Content-Type for FormData
        body: formDataToSend
      });

      console.log('Response status:', res.status);
      console.log('Response headers:', Object.fromEntries(res.headers.entries()));

      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        let errorData;
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await res.json();
        } else {
          const textResponse = await res.text();
          console.error('Non-JSON error response:', textResponse);
          errorData = { error: textResponse || `HTTP ${res.status}` };
        }
        
        console.error('Category creation failed:', errorData);
        throw new Error(errorData.details || errorData.error || errorData.message || `Failed to create category (${res.status})`);
      }

      const createdCategory = await res.json();
      console.log('Category created successfully:', createdCategory);
      console.log('Created category image URL:', createdCategory.category_image);

      resetNewCategoryForm();
      await fetchCategories(); // Wait for categories to refresh
      showToast('Category created successfully', 'success');
      
    } catch (err) {
      console.error('Error creating category:', err);
      console.error('Error stack:', err.stack);
      
      let errorMessage = 'Failed to create category';
      if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Could not connect to server. Make sure the backend is running.';
      } else if (err.message.includes('CORS')) {
        errorMessage = 'CORS error: Check server configuration.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setCreatingCategory(false);
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      setUpdating(true);
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('hsn_code', formData.hsn_code || '');

      // Ensure gst_rate is sent so backend NOT NULL constraint is satisfied
      const resolvedGstRate = formData.gst_rate !== '' && formData.gst_rate !== undefined && formData.gst_rate !== null
        ? formData.gst_rate
        : (editingCategory.gst_rate !== null && editingCategory.gst_rate !== undefined ? String(editingCategory.gst_rate) : '0');
      formDataToSend.append('gst_rate', resolvedGstRate);
      
      // Add image file if selected
      if (selectedImage) {
        console.log('Adding image to FormData:', selectedImage.name, selectedImage.size, selectedImage.type);
        formDataToSend.append('category_image', selectedImage);
      } else {
        console.log('No image selected for upload');
      }
      
      // Get headers without Content-Type (let browser set it for FormData)
      const headers = adminAuthHeaders();
      
      console.log('Sending update request with FormData...');
      const res = await fetch(`${API_BASE_URL}/api/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: headers, // Don't set Content-Type for FormData
        body: formDataToSend
      });

      console.log('Response status:', res.status);
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('Update failed:', data);
        const errorMsg = data.details || data.error || data.message || `Failed to update category (${res.status})`;
        throw new Error(errorMsg);
      }

      const result = await res.json();
      console.log('Update successful:', result);
      
      // Reset form and close edit form
      closeEditForm();
      
      fetchCategories(); // Refresh list
      showToast('Category updated successfully', 'success');
    } catch (e) {
      console.error('Error in handleUpdate:', e);
      showToast(e.message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (category) => {
    console.log('handleDeleteClick called for category:', category);
    // Set the category ID to show inline confirmation
    setCategoryToDelete(category.id);
  };

  // Cancel delete confirmation
  const cancelDelete = () => {
    setCategoryToDelete(null);
  };

  // Handle delete
  const handleDelete = async (categoryId) => {
    if (!categoryId) {
      console.error('No category ID provided for deletion');
      return;
    }

    try {
      setDeleting(true);
      console.log('Deleting category:', categoryId);
      
      const res = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: adminHeaders()
      });

      console.log('Delete response status:', res.status, res.statusText);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('Delete failed:', data);
        const errorMsg = data.error || data.message || `Failed to delete category (${res.status})`;
        throw new Error(errorMsg);
      }

      const result = await res.json();
      console.log('Delete successful:', result);

      setCategoryToDelete(null);
      fetchCategories(); // Refresh list
      showToast('Category deleted successfully', 'success');
    } catch (e) {
      console.error('Error in handleDelete:', e);
      showToast(e.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-categories">
      <div className="admin-categories-header">
        <div className="admin-categories-header-content">
          <h1>Categories Management</h1>
          <p>Manage product categories</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchCategories} disabled={loading}>
          <RefreshCw size={20} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Toast notifications */}
      {!!toasts.length && (
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.type}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="category-create-card">
        <div className="category-create-header">
          <div>
            <h2>Add New Category</h2>
            <p>Store the category name, HSN code, and display image directly in the catalog.</p>
          </div>
        </div>
        <form className="category-create-form" onSubmit={handleCreateCategory}>
          <div className="form-row">
            <div className="form-group">
              <label>Category Name *</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Fresh Produce"
                required
                disabled={creatingCategory}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>HSN Code</label>
              <input
                type="text"
                value={newCategory.hsn_code}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value.length <= 8) {
                    setNewCategory({ ...newCategory, hsn_code: value });
                  }
                }}
                maxLength="8"
                placeholder="Enter HSN code"
                disabled={creatingCategory}
              />
            </div>
            <div className="form-group">
              <label>GST Rate (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={newCategory.gst_rate}
                onChange={(e) => setNewCategory({ ...newCategory, gst_rate: e.target.value })}
                placeholder="e.g. 18 or 0"
                disabled={creatingCategory}
              />
            </div>
            <div className="form-group">
              <label>Category Image</label>
              <div className="file-upload-control">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => !creatingCategory && newCategoryImageInputRef.current?.click()}
                  disabled={creatingCategory}
                >
                  Choose File
                </button>
                <span className="file-upload-label">
                  {newCategoryImage ? newCategoryImage.name : 'No file chosen'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleNewCategoryImageChange}
                  disabled={creatingCategory}
                  className="file-input-hidden"
                  ref={newCategoryImageInputRef}
                />
              </div>
              <small className="file-hint">JPG, PNG or WEBP up to 5MB auto-optimize. GIF must be under 900KB.</small>
            </div>
          </div>
          {newCategoryPreview && (
            <div className="category-create-preview">
              <img src={newCategoryPreview} alt="New category" />
              {newCategoryImage && (
                <div className="preview-info">
                  Selected image: {newCategoryImage.name} ({(newCategoryImage.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>
          )}
          <div className="category-create-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={resetNewCategoryForm}
              disabled={creatingCategory}
            >
              Clear
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creatingCategory}
            >
              {creatingCategory ? 'Saving...' : 'Add Category'}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-categories-table-container">
        <table className="admin-categories-table">
          <thead>
            <tr>
              <th>Category Name</th>
              <th>HSN Code</th>
              <th>GST Rate</th>
              <th>Category Image</th>
              <th>Subcategories</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center' }}>
                  Loading...
                </td>
              </tr>
            )}
            {!loading && categories.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center' }}>
                  No categories found
                </td>
              </tr>
            )}
            {!loading && categories.map(category => (
              <React.Fragment key={category.id}>
                {/* Inline Edit Form - appears above the row when editing */}
                {editingCategoryId === category.id && (
                  <tr className="inline-edit-form-row">
                    <td colSpan={6} className="inline-edit-form-cell">
                      <div className="inline-edit-form-container">
                        <div className="inline-edit-form-header">
                          <h3>Edit Category: {category.name}</h3>
                          <button 
                            className="close-inline-btn" 
                            onClick={closeEditForm}
                            disabled={updating}
                            aria-label="Close edit form"
                          >
                            <X size={18} />
                          </button>
                        </div>
                        <form onSubmit={handleUpdate} className="inline-edit-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Category Name *</label>
                              <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                disabled={updating}
                              />
                            </div>
                            <div className="form-group">
                              <label>HSN Code</label>
                              <input
                                type="text"
                                value={formData.hsn_code}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  if (value.length <= 8) {
                                    setFormData({ ...formData, hsn_code: value });
                                  }
                                }}
                                maxLength="8"
                                disabled={updating}
                                placeholder="Enter HSN code"
                              />
                            </div>
                            <div className="form-group">
                              <label>GST Rate (%)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={formData.gst_rate}
                                onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                                disabled={updating}
                                placeholder="e.g. 18 or 0"
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Category Image</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              disabled={updating}
                              className="file-input"
                              id={`category-image-input-${category.id}`}
                            />
                            <small className="file-hint">JPG, PNG or WEBP up to 5MB auto-optimize. GIF must be under 900KB.</small>
                            {imagePreview && (
                              <div className="image-preview">
                                <img 
                                  src={imagePreview} 
                                  alt="Preview"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                                <div className="preview-error" style={{ display: 'none' }}>
                                  Invalid image
                                </div>
                                {selectedImage && (
                                  <div className="preview-info">
                                    New image selected: {selectedImage.name} ({(selectedImage.size / 1024).toFixed(2)} KB)
                                  </div>
                                )}
                                {!selectedImage && editingCategory?.category_image && (
                                  <div className="preview-info" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                                    Current image (no change)
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="inline-edit-form-actions">
                            <button 
                              type="button" 
                              className="btn btn-secondary" 
                              onClick={closeEditForm}
                              disabled={updating}
                            >
                              Cancel
                            </button>
                            <button 
                              type="submit" 
                              className="btn btn-primary" 
                              disabled={updating}
                            >
                              {updating ? 'Updating...' : 'Update Category'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </td>
                  </tr>
                )}
                {/* Regular Category Row */}
                <tr className={editingCategoryId === category.id ? 'row-being-edited' : ''}>
                  <td>
                    <div className="category-name-cell">
                      <strong>{category.name || 'N/A'}</strong>
                      {category.description && (
                        <div className="category-description">{category.description}</div>
                      )}
                    </div>
                  </td>
                  <td>{category.hsn_code || '-'}</td>
                  <td>{category.gst_rate !== null && category.gst_rate !== undefined ? `${Number(category.gst_rate)}%` : '-'}</td>
                  <td>
                    {category.category_image ? (
                      <img 
                        src={category.category_image} 
                        alt={category.name}
                        className="category-image"
                        onError={(e) => {
                          console.error('Image load error for category:', category.name);
                          console.error('Image URL:', category.category_image);
                          console.error('Error details:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully for category:', category.name);
                          console.log('Image URL:', category.category_image);
                        }}
                      />
                    ) : null}
                    <div className="no-image-placeholder" style={{ display: category.category_image ? 'none' : 'flex' }}>
                      <span>No Image</span>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="subcategory-toggle-btn"
                      onClick={() => toggleSubcategories(category)}
                    >
                      {expandedCategoryId === category.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span>{Number(category.subcategory_count || 0)} Subcategories</span>
                    </button>
                  </td>
                  <td>
                    <div className="admin-category-actions">
                      {categoryToDelete === category.id ? (
                        <div className="delete-confirmation-inline">
                          <div className="delete-confirm-message">
                            <p>Delete <strong>"{category.name}"</strong>?</p>
                            <p className="warning-text">This cannot be undone.</p>
                          </div>
                          <div className="delete-confirm-buttons">
                            <button 
                              type="button"
                              className="btn btn-secondary btn-sm" 
                              onClick={cancelDelete}
                              disabled={deleting}
                            >
                              Cancel
                            </button>
                            <button 
                              type="button"
                              className="btn btn-danger btn-sm" 
                              onClick={() => handleDelete(category.id)}
                              disabled={deleting}
                            >
                              {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button 
                            type="button"
                            className={`admin-action-btn edit ${editingCategoryId === category.id ? 'active' : ''}`}
                            title={editingCategoryId === category.id ? 'Close Edit' : 'Edit'} 
                            onClick={() => handleEdit(category)}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            type="button"
                            className="admin-action-btn delete" 
                            title="Delete" 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Delete button clicked for category:', category.id, category.name);
                              handleDeleteClick(category);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedCategoryId === category.id && (
                  <tr className="subcategory-panel-row">
                    <td colSpan={6}>
                      <div className="subcategory-panel">
                        <div className="subcategory-panel-header">
                          <div>
                            <h3>Subcategories for {category.name}</h3>
                            <p>Add optional product subcategories under this category.</p>
                          </div>
                        </div>

                        <form
                          className="subcategory-form"
                          onSubmit={(event) => handleSaveSubcategory(event, category.id)}
                        >
                          <div className="form-row">
                            <div className="form-group">
                              <label>Subcategory Name *</label>
                              <input
                                type="text"
                                value={subcategoryForm.name}
                                onChange={(event) => setSubcategoryForm({ ...subcategoryForm, name: event.target.value })}
                                placeholder="e.g., Rice, Oil, Snacks"
                                disabled={savingSubcategory}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Status</label>
                              <select
                                value={subcategoryForm.is_active ? 'active' : 'inactive'}
                                onChange={(event) => setSubcategoryForm({ ...subcategoryForm, is_active: event.target.value === 'active' })}
                                disabled={savingSubcategory}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Description</label>
                              <textarea
                                value={subcategoryForm.description}
                                onChange={(event) => setSubcategoryForm({ ...subcategoryForm, description: event.target.value })}
                                rows="2"
                                placeholder="Optional short description"
                                disabled={savingSubcategory}
                              />
                            </div>
                            <div className="form-group">
                              <label>Image/Icon</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleSubcategoryImageChange}
                                disabled={savingSubcategory}
                                className="file-input"
                                ref={subcategoryImageInputRef}
                              />
                              <small className="file-hint">Optional JPG, PNG or WEBP up to 5MB auto-optimize. GIF must be under 900KB.</small>
                            </div>
                          </div>
                          {subcategoryImagePreview && (
                            <div className="subcategory-preview">
                              <img src={subcategoryImagePreview} alt="Subcategory preview" />
                              <span>{subcategoryImage ? subcategoryImage.name : 'Current image'}</span>
                            </div>
                          )}
                          <div className="subcategory-form-actions">
                            {editingSubcategoryId && (
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={resetSubcategoryForm}
                                disabled={savingSubcategory}
                              >
                                Cancel Edit
                              </button>
                            )}
                            <button type="submit" className="btn btn-primary" disabled={savingSubcategory}>
                              {savingSubcategory
                                ? 'Saving...'
                                : editingSubcategoryId
                                ? 'Update Subcategory'
                                : 'Add Subcategory'}
                            </button>
                          </div>
                        </form>

                        <div className="subcategory-list">
                          {subcategoriesLoading && (
                            <div className="subcategory-empty">Loading subcategories...</div>
                          )}
                          {!subcategoriesLoading && (subcategoriesByCategory[category.id] || []).length === 0 && (
                            <div className="subcategory-empty">No subcategories added yet.</div>
                          )}
                          {!subcategoriesLoading && (subcategoriesByCategory[category.id] || []).map((subcategory) => (
                            <div className="subcategory-item" key={subcategory.id}>
                              <div className="subcategory-image-cell">
                                {subcategory.subcategory_image ? (
                                  <img src={subcategory.subcategory_image} alt={subcategory.name} />
                                ) : (
                                  <span>No Image</span>
                                )}
                              </div>
                              <div className="subcategory-copy">
                                <strong>{subcategory.name}</strong>
                                {subcategory.description && <p>{subcategory.description}</p>}
                              </div>
                              <span className={`subcategory-status ${Number(subcategory.is_active) ? 'active' : 'inactive'}`}>
                                {Number(subcategory.is_active) ? 'Active' : 'Inactive'}
                              </span>
                              <div className="subcategory-actions">
                                <button
                                  type="button"
                                  className="admin-action-btn edit"
                                  title="Edit subcategory"
                                  onClick={() => editSubcategory(subcategory)}
                                  disabled={savingSubcategory}
                                >
                                  <Edit2 size={15} />
                                </button>
                                <button
                                  type="button"
                                  className="admin-action-btn delete"
                                  title="Delete subcategory"
                                  onClick={() => handleDeleteSubcategory(category.id, subcategory.id)}
                                  disabled={deletingSubcategoryId === subcategory.id}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>


    </div>
  );
};

export default AdminCategoriesManagement;

